import * as React from "react";

import Menu from "@mui/material/Menu";
import { PopoverOrigin } from "@mui/material/Popover";

import { DashboardIconButton, DashboardIconButtonProps, ButtonSize } from "@/dashboard-design-system/DashboardButton";
import { MoreHorizontal } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface MenuCellProps {
  children: React.ReactNode[] | React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  icon?: IconElement;
  buttonSize?: ButtonSize;
  horizontal?: PopoverOrigin["horizontal"];
  buttonVariant?: DashboardIconButtonProps["variant"];
}

export const MenuCell: React.FunctionComponent<React.PropsWithChildren<MenuCellProps>> = ({
  children,
  icon,
  onClick,
  horizontal,
  buttonVariant,
  buttonSize = "small",
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );
  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const resolvedHorizontal = horizontal ?? "center";

  return (
    <div onClick={onClick}>
      <DashboardIconButton
        onClick={handleClick}
        size={buttonSize}
        vibe="neutral"
        variant={buttonVariant}
        data-test="menu-cell-icon-button"
      >
        {icon ?? <MoreHorizontal />}
      </DashboardIconButton>

      <Menu
        keepMounted
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: resolvedHorizontal }}
        transformOrigin={{ vertical: "top", horizontal: resolvedHorizontal }}
      >
        {children}
      </Menu>
    </div>
  );
};
